import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import {
  Loader2, ExternalLink, UserPlus, Clock, CheckCircle2,
  AlertCircle, Users, TrendingUp,
} from 'lucide-react'

function cleanGroupName(raw: string): string {
  return raw.replace(/[ÚU]ltima atividade.*/i, '').trim()
}

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PendingPost {
  id: string
  post_url: string | null
  post_type: string | null
  status: string
  invites_sent: number
  posted_at: string | null
  page_group: {
    id: string
    group_name: string
    group_url: string
    agent_connection_id: string
  } | null
}

interface GroupPost {
  id: string
  group_name: string | null
  group_url: string | null
  post_url: string | null
  status: string
  invites_sent: number
  posted_at: number | null
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function InvitesTab() {
  // Pending invites — posts that have been posted but not yet invited
  const { data: pendingRaw, isLoading: loadingPending } = useQuery<{ posts: PendingPost[] }>({
    queryKey: ['automacao-fb', 'pending-invites'],
    queryFn: () => api.get('/automacao-fb/group-posts/pending-invites'),
    refetchInterval: 15000,
  })

  // All posts for history (invited ones)
  const { data: allPostsRaw, isLoading: loadingPosts } = useQuery<{ posts: GroupPost[] }>({
    queryKey: ['automacao-fb', 'all-group-posts'],
    queryFn: () => api.get('/automacao-fb/group-posts'),
    refetchInterval: 30000,
  })

  const pendingPosts = pendingRaw?.posts ?? []
  const allPosts = allPostsRaw?.posts ?? []

  const invitedPosts = useMemo(
    () => allPosts.filter(p => p.status === 'invited' && p.invites_sent > 0),
    [allPosts],
  )

  // Stats
  const stats = useMemo(() => ({
    totalInvites: allPosts.reduce((sum, p) => sum + (p.invites_sent || 0), 0),
    pending: pendingPosts.length,
    completed: invitedPosts.length,
    avgInvites: invitedPosts.length > 0
      ? Math.round(invitedPosts.reduce((sum, p) => sum + p.invites_sent, 0) / invitedPosts.length)
      : 0,
  }), [allPosts, pendingPosts, invitedPosts])

  const isLoading = loadingPending || loadingPosts

  return (
    <div className="space-y-6">
      {/* ── Stats bar ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          icon={<UserPlus className="h-4 w-4 text-indigo-400" />}
          label="Total de convites"
          value={stats.totalInvites}
          accent="indigo"
        />
        <StatCard
          icon={<Clock className="h-4 w-4 text-amber-400" />}
          label="Posts pendentes"
          value={stats.pending}
          accent="amber"
        />
        <StatCard
          icon={<CheckCircle2 className="h-4 w-4 text-emerald-400" />}
          label="Posts convidados"
          value={stats.completed}
          accent="emerald"
        />
        <StatCard
          icon={<TrendingUp className="h-4 w-4 text-purple-400" />}
          label="Media por post"
          value={stats.avgInvites}
          accent="purple"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
        </div>
      ) : (
        <>
          {/* ── Pending invites ── */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-4 w-4 text-amber-400" />
              <h3 className="text-sm font-semibold text-zinc-300">Aguardando convite</h3>
              {pendingPosts.length > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 font-medium">
                  {pendingPosts.length}
                </span>
              )}
            </div>

            {pendingPosts.length === 0 ? (
              <div className="rounded-lg border border-[#27272a] bg-[#0c0c10] p-6 text-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-400/40 mx-auto mb-2" />
                <p className="text-xs text-zinc-600">Nenhum post aguardando convite</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pendingPosts.map(post => (
                  <div
                    key={post.id}
                    className="rounded-lg border border-amber-500/15 bg-amber-500/[0.03] p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-zinc-200 font-medium">{post.page_group?.group_name ? cleanGroupName(post.page_group.group_name) : '—'}</p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${
                            post.status === 'commented'
                              ? 'bg-blue-500/10 text-blue-400'
                              : 'bg-emerald-500/10 text-emerald-400'
                          }`}>
                            {post.status === 'commented' ? 'Comentado' : 'Postado'}
                          </span>
                          {post.posted_at && (
                            <span className="text-[10px] text-zinc-600">
                              {new Date(post.posted_at).toLocaleString('pt-BR', {
                                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                              })}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {post.post_url && (
                          <a
                            href={post.post_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#27272a] bg-[#111113] text-xs text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Ver post
                          </a>
                        )}
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600/20 border border-indigo-500/30">
                          <UserPlus className="h-3 w-3 text-indigo-400" />
                          <span className="text-xs text-indigo-300 font-medium">Proximo ciclo</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ── Completed invites ── */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              <h3 className="text-sm font-semibold text-zinc-300">Convites realizados</h3>
            </div>

            {invitedPosts.length === 0 ? (
              <div className="rounded-lg border border-[#27272a] bg-[#0c0c10] p-6 text-center">
                <Users className="h-6 w-6 text-zinc-700 mx-auto mb-2" />
                <p className="text-xs text-zinc-600">Nenhum convite realizado ainda</p>
              </div>
            ) : (
              <div className="rounded-lg border border-[#27272a] overflow-hidden">
                <div className="grid grid-cols-[1fr_100px_100px_40px] gap-3 px-4 py-2.5 bg-[#0c0c10] border-b border-[#27272a]">
                  <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Grupo</span>
                  <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Convites</span>
                  <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Data</span>
                  <span />
                </div>

                <div className="divide-y divide-[#1e1e22]">
                  {invitedPosts.map(post => (
                    <div
                      key={post.id}
                      className="grid grid-cols-[1fr_100px_100px_40px] gap-3 px-4 py-3 items-center hover:bg-[#111113] transition-colors"
                    >
                      <p className="text-xs text-zinc-300 truncate">{post.group_name ? cleanGroupName(post.group_name) : '—'}</p>

                      <div className="flex items-center gap-1.5">
                        <UserPlus className="h-3 w-3 text-indigo-400" />
                        <span className="text-xs text-indigo-300 font-medium">{post.invites_sent}</span>
                      </div>

                      <span className="text-[11px] text-zinc-600">
                        {post.posted_at
                          ? new Date(post.posted_at).toLocaleString('pt-BR', {
                              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                            })
                          : '—'
                        }
                      </span>

                      {post.post_url ? (
                        <a
                          href={post.post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-zinc-600 hover:text-indigo-400 transition-colors"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : (
                        <span />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function StatCard({
  icon,
  label,
  value,
  accent,
}: {
  icon: React.ReactNode
  label: string
  value: number
  accent: string
}) {
  return (
    <div className={`rounded-lg border border-${accent}-500/10 bg-${accent}-500/[0.03] p-4`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <p className="text-2xl font-bold text-zinc-200 font-mono">{value}</p>
    </div>
  )
}
